/**
 * src/utils/callCoordinator.js
 *
 * Browser-wide coordination of incoming calls / huddles across tabs.
 *
 * Every authenticated tab holds its own workspace WebSocket, so every tab receives the same
 * `call_offer` / `huddle_start`. Without coordination each tab rings, shows a banner, and can
 * answer — producing N ringtones and duplicate `call_answer` signals.
 *
 * Design (no new dependencies, built on browser primitives already used by auth):
 *
 *  1. Ring ownership — exactly one tab presents a given call. Ownership is a Web Lock named
 *     after the call (`synexos_call_ring:<key>`), requested with `ifAvailable`, so it is
 *     exclusive by construction (a BroadcastChannel message alone could not guarantee that).
 *     The lock is held while the call rings and is released by the browser automatically if
 *     the owning tab closes or crashes. Visible / focused tabs claim first.
 *  2. Owner fail-over — tabs that lost the claim queue on the same lock (bounded by the ring
 *     timeout). If the owner disappears before the call is resolved, the next tab takes over.
 *  3. Resolution — answer / decline / end / miss is recorded in a short-lived shared registry
 *     (localStorage) and broadcast. A resolved call is never presented or answered again, and
 *     any tab still showing it dismisses immediately.
 *  4. Busy — tabs that are in a call publish a heartbeat lease. A tab that wins a ring claim
 *     while ANOTHER tab is busy stays silent (the busy tab answers the caller with call_busy
 *     itself, exactly as before).
 *
 * Without Web Locks a localStorage claim (write, then confirm) is used; the backend remains
 * authoritative for call state (call_end / call_reject / call_busy events still arrive).
 */
import logger from "./logger";

export const CALL_COORD_CHANNEL = "synexos_call_coord";
export const CALL_RESOLVED_STORAGE_KEY = "synexos_call_resolved";
export const CALL_BUSY_STORAGE_KEY = "synexos_call_busy_tabs";
export const CALL_HANDOFF_PREFIX = "synexos_call_handoff:";
const CLAIM_PREFIX = "synexos_call_ring_claim:";

export const RING_CLAIM_TTL_MS = 35000;
export const RESOLVED_RETENTION_MS = 2 * 60 * 1000;
export const BUSY_HEARTBEAT_MS = 10000;
export const BUSY_LEASE_EXPIRY_MS = 35000;
// Covers the pre-join screen: the descriptor is consumed when the meeting room mounts.
export const HANDOFF_TTL_MS = 5 * 60 * 1000;

export const TAB_ID = `tab_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

// ─── Small storage helpers ─────────────────────────────────────────────────────

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    if (!value || (typeof value === "object" && Object.keys(value).length === 0)) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    /* ignore */
  }
}

// ─── Transport (BroadcastChannel, storage-event fallback) ─────────────────────

let channel = null;
let channelInit = false;
const listeners = new Set();
const FALLBACK_EVENT_KEY = "synexos_call_coord_event";

function deliver(msg) {
  if (!msg || typeof msg !== "object" || msg.tab === TAB_ID) return;
  listeners.forEach((fn) => {
    try {
      fn(msg);
    } catch (err) {
      logger.warn("[CallCoordinator] listener error", err?.message || err);
    }
  });
}

function ensureChannel() {
  if (channelInit || typeof window === "undefined") return channel;
  channelInit = true;
  if (typeof window.BroadcastChannel === "function") {
    try {
      channel = new BroadcastChannel(CALL_COORD_CHANNEL);
      channel.onmessage = (e) => deliver(e?.data);
    } catch {
      channel = null;
    }
  }
  window.addEventListener("storage", (e) => {
    if (e.key === FALLBACK_EVENT_KEY && e.newValue) {
      try {
        deliver(JSON.parse(e.newValue));
      } catch {
        /* ignore */
      }
    } else if (e.key === CALL_RESOLVED_STORAGE_KEY && e.newValue) {
      // Registry write from a tab without BroadcastChannel: surface new resolutions.
      const map = readJson(CALL_RESOLVED_STORAGE_KEY, {});
      Object.keys(map).forEach((key) => deliver({ type: "resolved", key, outcome: map[key]?.outcome }));
    }
  });
  return channel;
}

function post(msg) {
  const full = { ...msg, tab: TAB_ID, ts: Date.now() };
  const ch = ensureChannel();
  if (ch) {
    try {
      ch.postMessage(full);
      return;
    } catch {
      /* fall through */
    }
  }
  try {
    localStorage.setItem(FALLBACK_EVENT_KEY, JSON.stringify(full));
  } catch {
    /* ignore */
  }
}

export function subscribeCallCoordination(fn) {
  if (typeof fn !== "function") return () => {};
  ensureChannel();
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ─── Call identity ─────────────────────────────────────────────────────────────

/**
 * Stable key for one ringing call as seen by every tab of the callee.
 * kind: "call" | "huddle" | "video_huddle".
 */
export function callKey({ kind = "call", chat_id, from_user_id, host_id, meeting_id, call_instance_id } = {}) {
  const who = from_user_id || host_id || "";
  const base = `${kind}:${chat_id || ""}:${who}:${meeting_id || ""}`;
  // Instance-aware backend: every call attempt gets its own key. Chat calls reuse one meeting id,
  // so without it the resolution of the previous attempt silenced a rapid redial for
  // DUPLICATE_OFFER_WINDOW_MS. Copies of the SAME attempt (two sockets, several tabs) still share it.
  return call_instance_id ? `${base}:${call_instance_id}` : base;
}

// ─── Resolution registry ───────────────────────────────────────────────────────

function pruneResolved(map, now = Date.now()) {
  let changed = false;
  Object.keys(map).forEach((k) => {
    if (!map[k] || now - Number(map[k].at || 0) > RESOLVED_RETENTION_MS) {
      delete map[k];
      changed = true;
    }
  });
  return changed;
}

/**
 * Call keys are not unique per call: two consecutive calls from the same person in the same
 * chat (legacy P2P offers carry no meeting id, huddles never do) share a key. A resolution
 * therefore only covers the offer it belongs to: duplicate deliveries of ONE offer (several
 * sockets / tabs) arrive within moments of each other, while a new call from the same person
 * arrives later. A resolution recorded more than this long before an offer arrived belongs to
 * an earlier call and is ignored (and cleared).
 */
export const DUPLICATE_OFFER_WINDOW_MS = 8000;

function resolvedEntry(key) {
  const map = readJson(CALL_RESOLVED_STORAGE_KEY, {});
  return map[key] || null;
}

/**
 * Has the call identified by `key` already been resolved?
 * @param {string} key
 * @param {number} [offerAt] When this tab received the offer. When given, only a resolution
 *        that is not older than the offer (minus the duplicate window) counts.
 */
export function isCallResolved(key, offerAt) {
  if (!key) return false;
  const entry = resolvedEntry(key);
  if (!entry) return false;
  const at = Number(entry.at || 0);
  if (Date.now() - at > RESOLVED_RETENTION_MS) return false;
  if (typeof offerAt === "number") return at >= offerAt - DUPLICATE_OFFER_WINDOW_MS;
  return true;
}

function clearStaleResolution(key, offerAt) {
  const map = readJson(CALL_RESOLVED_STORAGE_KEY, {});
  const entry = map[key];
  if (entry && Number(entry.at || 0) < offerAt - DUPLICATE_OFFER_WINDOW_MS) {
    delete map[key];
    writeJson(CALL_RESOLVED_STORAGE_KEY, map);
  }
}

/**
 * Record that a call was answered / declined / ended / missed, release this tab's ring
 * ownership, and tell every other tab to dismiss it. Idempotent for one call; a resolution
 * left over from an EARLIER call with the same key is replaced.
 */
export function resolveCall(key, outcome = "ended") {
  if (!key) return;
  const now = Date.now();
  const map = readJson(CALL_RESOLVED_STORAGE_KEY, {});
  pruneResolved(map, now);
  const existing = map[key];
  if (!existing || now - Number(existing.at || 0) > DUPLICATE_OFFER_WINDOW_MS) {
    map[key] = { outcome, at: now, tab: TAB_ID };
    writeJson(CALL_RESOLVED_STORAGE_KEY, map);
  }
  releaseRing(key);
  post({ type: "resolved", key, outcome });
}

/**
 * Atomically-enough claim the right to ANSWER a call: refuses when this call was already
 * resolved by any tab, otherwise marks it answered before the caller sends call_answer.
 * @param {string} key
 * @param {number} [offerAt] When the offer being answered arrived (see isCallResolved).
 */
export function claimAnswer(key, offerAt) {
  if (!key) return true;
  if (isCallResolved(key, offerAt)) return false;
  resolveCall(key, "answered");
  return true;
}

// ─── Ring ownership ────────────────────────────────────────────────────────────

const held = new Map(); // key -> { release: fn, waiter?: AbortController }

function locksApi() {
  return typeof navigator !== "undefined" && navigator.locks && typeof navigator.locks.request === "function"
    ? navigator.locks
    : null;
}

function claimDelayMs() {
  if (typeof document === "undefined") return 0;
  const visible = document.visibilityState === "visible";
  const focused = typeof document.hasFocus === "function" ? document.hasFocus() : visible;
  if (visible && focused) return 0;
  if (visible) return 120;
  return 300;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function holdLock(locks, name, key, ttlMs, ifAvailable, signal) {
  return new Promise((resolveClaim) => {
    let settled = false;
    const options = ifAvailable ? { ifAvailable: true } : { signal };
    locks
      .request(name, options, (lock) => {
        if (!lock) {
          settled = true;
          resolveClaim(false);
          return undefined;
        }
        return new Promise((release) => {
          const timer = setTimeout(() => {
            held.delete(key);
            release();
          }, ttlMs);
          held.set(key, {
            release: () => {
              clearTimeout(timer);
              held.delete(key);
              release();
            },
          });
          settled = true;
          resolveClaim(true);
        });
      })
      .catch(() => {
        if (!settled) resolveClaim(false);
      });
  });
}

function storageClaim(key, ttlMs) {
  const k = CLAIM_PREFIX + key;
  const now = Date.now();
  try {
    const raw = localStorage.getItem(k);
    if (raw) {
      const [ts, owner] = raw.split("|");
      if (owner !== TAB_ID && now - Number(ts) < ttlMs) return false;
    }
    localStorage.setItem(k, `${now}|${TAB_ID}`);
    const confirmed = localStorage.getItem(k) || "";
    if (confirmed.split("|")[1] !== TAB_ID) return false;
    const timer = setTimeout(() => releaseRing(key), ttlMs);
    held.set(key, {
      release: () => {
        clearTimeout(timer);
        held.delete(key);
        try {
          if ((localStorage.getItem(k) || "").split("|")[1] === TAB_ID) localStorage.removeItem(k);
        } catch {
          /* ignore */
        }
      },
    });
    return true;
  } catch {
    return true; // storage unusable: behave like a single tab (previous behaviour)
  }
}

/**
 * Try to become the one tab that presents (rings / shows) this call.
 *
 * @param {string} key  callKey(...)
 * @param {object} [opts]
 * @param {number} [opts.ttlMs]       How long the claim may be held (ring timeout).
 * @param {Function} [opts.onTakeover] Called later if this tab lost the claim but the owner
 *        went away (closed / crashed) before the call was resolved.
 * @returns {Promise<boolean>} true when this tab owns the ring.
 */
export async function claimRing(key, { ttlMs = RING_CLAIM_TTL_MS, onTakeover, offerAt = Date.now() } = {}) {
  if (!key) return true;
  if (held.has(key)) return true;
  // A resolution of an earlier call that happened to share this key must not silence a new one.
  clearStaleResolution(key, offerAt);
  if (isCallResolved(key, offerAt)) return false;

  const delay = claimDelayMs();
  if (delay) await sleep(delay);
  if (isCallResolved(key, offerAt)) return false;

  const locks = locksApi();
  const name = `synexos_call_ring:${key}`;
  if (!locks) return storageClaim(key, ttlMs);

  const owned = await holdLock(locks, name, key, ttlMs, true);
  if (owned || typeof onTakeover !== "function") return owned;

  // Queue for fail-over: acquires only when the current owner releases (resolved, timed out,
  // or its tab closed). A resolved call aborts the wait.
  const startedAt = Date.now();
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const abortTimer = setTimeout(() => controller?.abort(), ttlMs);
  const unsub = subscribeCallCoordination((msg) => {
    if (msg.type === "resolved" && msg.key === key) controller?.abort();
  });
  holdLock(locks, name, key, Math.max(1000, ttlMs - (Date.now() - startedAt)), false, controller?.signal).then(
    (got) => {
      clearTimeout(abortTimer);
      unsub();
      if (!got) return;
      if (isCallResolved(key, offerAt) || Date.now() - startedAt >= ttlMs) {
        releaseRing(key);
        return;
      }
      logger.debug("[CallCoordinator] ring owner went away; taking over", key);
      try {
        onTakeover();
      } catch {
        releaseRing(key);
      }
    }
  );
  return false;
}

export function releaseRing(key) {
  const h = held.get(key);
  if (h) h.release();
}

export function ownsRing(key) {
  return held.has(key);
}

// ─── Busy leases ───────────────────────────────────────────────────────────────

let busyTimer = null;
const busySources = new Set();

function writeBusy(active) {
  const map = readJson(CALL_BUSY_STORAGE_KEY, {});
  const now = Date.now();
  Object.keys(map).forEach((t) => {
    if (now - Number(map[t]) > BUSY_LEASE_EXPIRY_MS) delete map[t];
  });
  if (active) map[TAB_ID] = now;
  else delete map[TAB_ID];
  writeJson(CALL_BUSY_STORAGE_KEY, map);
}

/**
 * Publish (or withdraw) this tab's "in a call" lease. Idempotent. Several independent call
 * engines can live in one tab (Connect callState, the Chat module's P2P call), so busy is
 * tracked per `source` and the lease is withdrawn only when no source is busy.
 */
export function setTabCallBusy(active, source = "default") {
  if (active) busySources.add(source);
  else busySources.delete(source);
  if (busySources.size > 0) {
    writeBusy(true);
    if (!busyTimer) busyTimer = setInterval(() => writeBusy(true), BUSY_HEARTBEAT_MS);
  } else {
    if (busyTimer) {
      clearInterval(busyTimer);
      busyTimer = null;
    }
    writeBusy(false);
  }
}

/** True when a tab OTHER than this one holds a live busy lease. */
export function isAnotherTabCallBusy() {
  const map = readJson(CALL_BUSY_STORAGE_KEY, {});
  const now = Date.now();
  return Object.keys(map).some((t) => t !== TAB_ID && now - Number(map[t]) <= BUSY_LEASE_EXPIRY_MS);
}

if (typeof window !== "undefined") {
  // Withdraw the lease promptly when the tab goes away (crash is covered by expiry).
  window.addEventListener("pagehide", () => {
    try {
      writeBusy(false);
    } catch {
      /* ignore */
    }
  });
}

// ─── Call hand-off to a newly opened meeting tab ──────────────────────────────

/**
 * Hand the "this call is active" state to the tab that will actually host the meeting.
 * The opener must not keep it (that left the opener permanently busy — audit H2).
 */
export function writeCallHandoff(meetingId, descriptor) {
  if (!meetingId) return;
  try {
    localStorage.setItem(CALL_HANDOFF_PREFIX + meetingId, JSON.stringify({ descriptor, at: Date.now() }));
  } catch {
    /* ignore */
  }
}

/** Consume (read + delete) a fresh hand-off for this meeting, or null. */
export function takeCallHandoff(meetingId) {
  if (!meetingId) return null;
  const k = CALL_HANDOFF_PREFIX + meetingId;
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    localStorage.removeItem(k);
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - Number(parsed.at || 0) > HANDOFF_TTL_MS) return null;
    return parsed.descriptor || null;
  } catch {
    return null;
  }
}

/** Test-only reset. */
export function __resetCallCoordinatorForTests() {
  held.forEach((h) => {
    try {
      h.release();
    } catch {
      /* ignore */
    }
  });
  held.clear();
  listeners.clear();
  busySources.clear();
  if (busyTimer) {
    clearInterval(busyTimer);
    busyTimer = null;
  }
}
